import { memo, useCallback } from 'react'

import { useRouter } from 'next/router'

import { BestChoice } from 'modules/best-choice'
import { Layout } from 'modules/layout'
import { PropertyBackedLoanHero } from 'modules/property-backed-loan-hero'
import { buildSimulatorUrl } from 'utils/buildSimulatorUrl'
import { pushDataLayer } from 'utils/push-data-layer'


import { HomeRenovationBenefits } from './benefits'
import { homeRenovationHeroContent } from './content'
import { HomeRenovationConversionCta } from './conversion-cta'
import { HomeRenovationHelp } from './help'
import { HomeRenovationHowItWorks } from './how-it-works'
import { HomeRenovationOtherProducts } from './other-products'
import { HomeRenovationSimulationSteps } from './simulation-steps'
import * as S from './styles'
import { HomeRenovationWaysToUse } from './ways-to-use'
import { HomeRenovationWorthIt } from './worth-it'

type SimulatorSection =
  | 'Como funciona'
  | 'Vantagens'
  | 'Como usar'
  | 'Passos da simulação'
  | 'Vale a pena'
  | 'Melhor escolha'
  | 'Transforme sua casa'
  | 'FAQ'

const pageName = 'Empréstimo para reforma de casa'

export const HomeRenovationLoan = memo(() => {
  const router = useRouter()

  const handleSimulatorClick = useCallback(
    (section: SimulatorSection) => {
      pushDataLayer({
        category: 'Clique',
        action: 'Botões',
        label: `${section} - ${pageName} - Simular agora`
      })

      location.href = buildSimulatorUrl(router.pathname, { medium: `${section}-Simular agora` })
    },
    [router.pathname]
  )

  const handleHowItWorksClick = useCallback(() => handleSimulatorClick('Como funciona'), [handleSimulatorClick])
  const handleBenefitsClick = useCallback(() => handleSimulatorClick('Vantagens'), [handleSimulatorClick])
  const handleWaysToUseClick = useCallback(() => handleSimulatorClick('Como usar'), [handleSimulatorClick])
  const handleSimulationStepsClick = useCallback(
    () => handleSimulatorClick('Passos da simulação'),
    [handleSimulatorClick]
  )
  const handleWorthItClick = useCallback(() => handleSimulatorClick('Vale a pena'), [handleSimulatorClick])
  const handleBestChoiceClick = useCallback(() => handleSimulatorClick('Melhor escolha'), [handleSimulatorClick])
  const handleConversionClick = useCallback(() => handleSimulatorClick('Transforme sua casa'), [handleSimulatorClick])
  const handleHelpClick = useCallback(() => handleSimulatorClick('FAQ'), [handleSimulatorClick])

  return (
    <Layout darkWarning>
      <PropertyBackedLoanHero
        dataLayerPageName={pageName}
        content={homeRenovationHeroContent}
        variant="home-renovation"
      />
      <HomeRenovationHowItWorks onClick={handleHowItWorksClick} />
      <HomeRenovationBenefits onClick={handleBenefitsClick} />
      <HomeRenovationWaysToUse onClick={handleWaysToUseClick} />
      <HomeRenovationSimulationSteps onClick={handleSimulationStepsClick} />
      <HomeRenovationWorthIt onClick={handleWorthItClick} />
      <S.BestChoiceWrapper>
        <BestChoice onClick={handleBestChoiceClick} variant="contrast" ctaLabel="Simular agora" />
      </S.BestChoiceWrapper>
      <HomeRenovationConversionCta onClick={handleConversionClick} />
      <HomeRenovationHelp onClick={handleHelpClick} />
      <HomeRenovationOtherProducts />
    </Layout>
  )
})

HomeRenovationLoan.displayName = 'HomeRenovationLoan'
